
import React, { useState, useMemo, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { INVENTORY_STATUSES, AUTO_ORDER_STATUSES } from 'constants/statuses'
/**
 * Plenty / Low / Critical from CAPACITY vs the alert threshold — the same arithmetic the
 * level gauge plots, so the chip can never disagree with the bar next to it. Ordered and
 * Incoming are excluded on purpose: a tag still at Vera, or in a van, cannot be applied to
 * a product today, so counting either would let a brand holding nothing read green.
 *
 * This used to be a local `inventoryStatus` here — one of THREE hand-rolled copies (this
 * file, `InventoryLevelCell`, `TagInventoryPage`), and the gauge's copy was the one that
 * could fall through to `''` and then throw on the palette lookup. Imported now, per
 * working-conventions §36: one derivation, so "the same arithmetic" is guaranteed by
 * construction rather than asserted in a comment.
 */
import { inventoryStatus, inventoryStatusMeta, type InventoryLike } from 'utils/inventory'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'

import { useCanAccess, useCurrentCompany, useReferrerState } from 'hooks'
import { useTagInventoryOverview } from 'hooks/api/useTagInventoryOverview'
// BadgeAlertIcon / ShoppingBagIcon dropped along with the card glyphs they were.
import { TagIcon, PlusIcon, ShoppingCartIcon, Settings2Icon } from 'lucide-react'
import { ROUTES } from 'routes'

// Card tooltips are plain strings since VERA-455 (one sentence, no formula block), so the
// Box/Typography wrappers they used to need are gone, and with them the last use of several
// imports that had been dead here for a while.
import { Grid, Typography } from '@mui/material'
import { Button } from '@repo/ui'
import { Table, Loader, EmptyState, ActionDropdown, ProductCell, ProductGroupCell, InventoryLevelCell, StatusChipCell, AnalyticsBlock, IntroDataBanner, SetInventorySettingsModal, SetProductsForTagModal } from 'components'
import { headerWithTerm } from 'components/display/Table'




export const Inventory = () => {
  const navigate = useNavigate()
  // Derived from the Tags page URL this tab renders under — "← Back to Tags".
  const referrer = useReferrerState()
  const { company } = useCurrentCompany()
  // Standing rule (2026-08-12): permission gates a kebab item as disabled+tooltip,
  // never `visible` (§27). This was previously commented out entirely (both the
  // gate and its dep array below), which briefly left "Inventory Settings"
  // unconditionally visible to everyone. `tags_inventory_settings` is the correct
  // permission here — the item that was there before, `isCanTransfer`
  // (`transfer_certificate`), gates certificate transfer and has nothing to do
  // with tag inventory; it was almost certainly a copy-paste leftover.
  const { isHasAccess: isCanManageInventorySettings } = useCanAccess({ action: 'tags_inventory_settings' })

  const [selectedInventory, setSelectedInventory] = useState(null)
  const [openInventorySettingsModal, setOpenInventorySettingsModal] = useState(false)
  const [openSetProductsForTagModal, setOpenSetProductsForTagModal] = useState(false)

  // VRG-90: real inventory data (cards + rows) for the company.
  const { cards, rows, isLoading, refetch } = useTagInventoryOverview()
  const inventory = rows

  const handleSettings = (row) => {
    setSelectedInventory(row)
    setOpenInventorySettingsModal(true)
  }

  const handleAddProduct = (row) => {
    setSelectedInventory(row)
    setOpenSetProductsForTagModal(true)
  }

  const getActionItems = useCallback(
    (row) => {
        return [
          { 
            label: 'Purchase Tags', 
            onClick: () => navigate(ROUTES.TAGS_SHOP_PRODUCT(row.tag_shop_product?.id || row.id), { state: referrer }),
            icon: <ShoppingCartIcon strokeWidth={1}/> 
          },
          {
            label: 'Inventory Settings',
            onClick: () => handleSettings(row),
            disabled: !isCanManageInventorySettings,
            tooltip: isCanManageInventorySettings ? undefined : "You need the 'Inventory settings' permission. Ask an admin to grant it.",
            icon: <Settings2Icon strokeWidth={1}/>
          },
          {
            label: 'Assign products',
            onClick: () =>  handleAddProduct(row),
            icon: <PlusIcon strokeWidth={1}/>
          },
      ]
    },
    [handleSettings, handleAddProduct, isCanManageInventorySettings, navigate, referrer]
  )

 const columns = useMemo(() => [
      {
        field: 'id',
        headerName: 'Tag',
        type: 'string',
        // This table has no bulk-select toggle (no `bulkActions` passed), so its
        // ActionLine's search box sits ~70px further left than on tables that do
        // — 298px (not the 368px house default) is what actually lines the
        // column's right border up with the search box's right edge here.
        width: 298,
        pinned: 'left',
        renderCell: ({ row }) => (<ProductCell product={row.tag_shop_product} route={ROUTES.TAG_INVENTORY(row.tag_shop_product.sku)} width={210} />),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'statusChip',
        options: statusFilterOptions(INVENTORY_STATUSES),
        width: 150,
        // Derived from CAPACITY (Available − uncertified demand) against the threshold, so
        // the pill and the level gauge beside it always tell the same story. It used to read
        // `quantity − Σ for_products.quantity`: the deprecated lifetime ordered counter,
        // against ALL assigned products rather than the uncertified ones the gauge uses — two
        // different sums, so the chip could say "Plenty" over a bar drawn in the red zone.
        valueGetter: (row, value) => (row ? inventoryStatus(row) : value),
        renderCell: ({ row, value }) => (
          <StatusChipCell
            status={row ? inventoryStatus(row) : value}
            statuses={INVENTORY_STATUSES}
          />
        ),
      },
      // ── The three POSSESSION stages, in pipeline order: Ordered -> Incoming -> Available ──
      //
      // All three come from `tags.possession_state`, so a tag sits in exactly one of them and
      // the three ADD UP. That reconciliation is the point. "Ordered" used to render
      // `company_tag_inventory.ordered_qty`, a LIFETIME purchase counter nothing decrements —
      // it could never square with a possession count, which is precisely why a row reading
      // "Ordered 100 / On hand 0" looked broken rather than informative.
      {
        field: 'at_vera_qty',
        headerName: 'Ordered',
        type: 'number',
        // +18px over the bare-text measurement for the term glyph (14px icon + 4px gap),
        // per headerWithTerm's warning and §49 — a header is never truncated.
        width: 140,
        renderHeader: headerWithTerm('Ordered', 'inventory-ordered'),
      },
      {
        // HIDDEN by default. Ordered and Available are persistent facts about a SKU — a
        // brand's tags are nearly always in one or the other. Incoming is transient: non-zero
        // only while a shipment is actually in flight, so per row it reads 0 on almost every
        // row almost all the time, and a column that is empty by default earns hiding. The
        // AGGREGATE still earns a card, because "is anything arriving right now" is exactly
        // what a brand with nothing on hand needs to know before concluding it is stuck.
        field: 'incoming_qty',
        headerName: 'Incoming',
        type: 'number',
        // +18px over the bare-text measurement for the term glyph (14px icon + 4px gap),
        // per headerWithTerm's warning and §49 — a header is never truncated.
        width: 150,
        renderHeader: headerWithTerm('Incoming', 'inventory-incoming'),
      },
      {
        // "Available" now means possession and nothing else. It previously named SPARE
        // CAPACITY on this surface (a card and a tooltip row), which is why this column was
        // called "On hand"; capacity is now called Capacity, so the word is free and the
        // column matches the state it counts (`possession_state = 'available'`).
        field: 'available_qty',
        headerName: 'Available',
        type: 'number',
        // +18px over the bare-text measurement for the term glyph (14px icon + 4px gap),
        // per headerWithTerm's warning and §49 — a header is never truncated.
        width: 150,
        renderHeader: headerWithTerm('Available', 'inventory-available'),
      },
      {
        // A GAUGE, not a count. Plots FOUR values — Ordered, Available, Threshold, Uncertified
        // products — so it is deliberately NOT renamed "Capacity": naming a four-variable
        // chart after one of its variables would tell the reader the bar means one thing when
        // it means four, which is a small instance of the conflation this whole pass unwinds.
        // Shortened from "Inventory levels" per §25.
        field: 'level',
        headerName: 'Levels',
        type: 'string',
        filterable: false,
        width: 300,
        renderCell: ({ row }) => (<InventoryLevelCell inventory={row} />)
      },
      {
        // HIDDEN by default, for the same reason as Incoming: the gauge to its left ALREADY
        // conveys this number visually — the fill against the threshold tick IS capacity,
        // rendered. The numeric column earns its place for sorting and exact reads, not for
        // scanning.
        field: 'capacity',
        headerName: 'Capacity',
        type: 'number',
        // +18px over the bare-text measurement for the term glyph (14px icon + 4px gap),
        // per headerWithTerm's warning and §49 — a header is never truncated.
        width: 150,
        // The definition earns its place here more than on any other column: Capacity is
        // the only SIGNED figure on the row, and "-26" reads as a broken number rather than
        // "short by 26" unless something says so.
        renderHeader: headerWithTerm('Capacity', 'inventory-capacity'),
        // Tinted with the SAME status colour the gauge to its left paints its bar with, from
        // the same `inventoryStatus` derivation — so a red bar can never sit beside a number
        // in default black. Reading "-26" as neutral body text next to a red bar invited the
        // conclusion that the number was the broken thing rather than the level it reports.
        //
        // `.color` (not `.bar`): `bar` is tuned for a filled shape against a grey track and is
        // too light to read as TEXT — the same distinction the `inventoryBarColor` docblock in
        // `Cells.tsx` draws from the other side. `inventoryStatusMeta` is used rather than
        // indexing `INVENTORY_STATUSES`, so an unexpected status renders a colour instead of
        // throwing inside the grid.
        // Params are typed here rather than left implicit: the surrounding file leans on
        // `any` throughout, but a column this one derives a COLOUR from is the wrong place
        // to add to that pile — `inventoryStatus` reads specific fields off `row`.
        renderCell: ({ row, value }: { row: InventoryLike; value: number | null }) => (
          <Typography
            variant="body2"
            sx={{ color: inventoryStatusMeta(inventoryStatus(row)).color }}
          >
            {value}
          </Typography>
        ),
      },
      {
        // HIDDEN. Demand, and the other half of Capacity's arithmetic. It was a KPI card
        // ("Uncertified products") until the five possession cards took the row; kept here so
        // the figure is still reachable per SKU rather than silently dropped, and it remains
        // on the gauge tooltip.
        field: 'uncertified_qty',
        // "Uncertified" alone did not say uncertified WHAT — on a table whose every other
        // column counts TAGS, it read as a fourth tag state beside Ordered/Incoming/Available.
        // It counts PRODUCT UNITS awaiting a certificate: this SKU's demand, not its supply.
        headerName: 'Uncertified products',
        type: 'number',
        // 130 could not fit this header even BEFORE the glyph — the label alone measures
        // wider than the column, so it was truncating whenever the column was un-hidden.
        // 225 is the measured requirement exactly: 127px of text (Lato 14) + 18 for the
        // term glyph and its gap + 80 of grid header chrome.
        width: 225,
        renderHeader: headerWithTerm('Uncertified products', 'inventory-uncertified-products'),
      },
      {
        field: 'products',
        // Was "Product inventory", which collided with the page's own subject — this table IS
        // an inventory, of tags, so "Product inventory" read as another tag figure. It sums
        // the STOCK of the products this tag is assigned to, which is why it is the number
        // Uncertified products is a subset of.
        headerName: 'Units in stock',
        type: 'number',
        // 185. Measured in the browser rather than reasoned about, per §49 — and the
        // arithmetic version of this was 180, which is ONE PIXEL under what the header
        // actually needs. Against the rendered font (Lato 14) "Units in stock" is 83px,
        // the term glyph and its gap are exactly 18, and the grid's own header chrome is
        // 80: 181. A column hidden by default is the easiest place for a 1px clip to
        // survive review, since nobody sees it until they turn the column on.
        width: 185,
        renderHeader: headerWithTerm('Units in stock', 'inventory-units-in-stock'),
        renderCell: ({ row }) => (row.for_products.reduce((sum, obj) => sum + (obj['quantity'] || 0), 0))
      },
      {
        // VISIBLE now, and shortened from "Status threshold" (§25). It is the number the
        // Status pill and the gauge's tick both answer to, so hiding it left the two most
        // prominent things on the row explained by a value the operator could not see.
        field: 'threshold',
        headerName: 'Threshold',
        type: 'number',
        // 140, measured: at 120 the header truncated to "Thresh…". Same precedent as
        // ProductsPage's Inventory column — a number column is sized by its HEADER (title
        // text + sort icon + gap + cell padding), never by its values.
        // 160, not 140: the measurement below was taken against the bare word, and the
        // term glyph adds 18px to it.
        width: 160,
        renderHeader: headerWithTerm('Threshold', 'inventory-threshold'),
      },
      {
        field: 'auto_order',
        headerName: 'Auto-order',
        type: 'boolean',
        // 100 truncated the header to "Aut…". Measured: the title text is 69px and the header's
        // own chrome (padding + sort/menu affordances) takes 63px of the column, leaving 37px
        // for a 69px word. 135 fits it with room for the sort arrow.
        width: 135,
        // A readable On/Off status pill (matches the "Plenty" status chip) instead
        // of a bare × which was hard to read.
        renderCell: ({ value }) => (
          <StatusChipCell status={value ? 'on' : 'off'} statuses={AUTO_ORDER_STATUSES} />
        ),
      },
      {
        field: 'for_products',
        headerName: 'For products',
        type: 'multiSelect',
        width: 250,
        renderCell: ({ row }) => (<ProductGroupCell products={row.for_products} route={ROUTES.PRODUCT} />),
      },
      {
        field: 'actions',
        headerName: false,
        align: 'center',
        width: 54,
        pinned: 'right',
        renderCell: ({ row }) => <ActionDropdown items={getActionItems(row)} />,
      },
     ],[] 
  ) 


  // Card copy: ONE short sentence each, plain nouns. The old tooltips paired a long
  // sentence with a monospace restatement of the formula ("count of tag SKUs where spare
  // capacity ≤ level threshold"), which is jargon dressed as precision — it explains the
  // arithmetic to someone who already understood the label and nothing to anyone else. The
  // per-SKU arithmetic still breaks out on the gauge's own tooltip, next to the bar that
  // plots it, which is where a formula belongs.
  //
  // The three possession cards no longer carry local copy at all: their definitions are
  // `inventory-ordered` / `-incoming` / `-available` in the glossary, which is the same
  // sentence the COLUMN header beside them now shows. Two hand-typed strings saying what
  // one glossary entry says is precisely the drift §44 exists to prevent — and a card and
  // a column disagreeing about what "Available" means is the version of it that costs a
  // brand an order.
  // Names the flooring, because the card and the Capacity column genuinely do not sum to each
  // other and the old wording ("Available, minus products still waiting for one") read as a
  // plain column total. A SKU that is short does not eat into another SKU's spare tags — they
  // are different tags — so a shortfall contributes 0 here rather than a negative. The server
  // comment at `get-tag-inventory-overview.use-case.ts` carries the full argument.
  // Kept as local text, and this is the case the `term` + `text` combination exists for.
  // The CARD floors each tag type at zero; the COLUMN is signed. `inventory-capacity`
  // defines the signed figure, so showing its `short` here would promise negatives on a
  // card that can never render one. Local body, glossary footer.
  const capacityTip = 'How many more products your tags can certify, added up across tag types. A tag type that is short counts as zero here, not as a negative: its shortfall cannot be covered by spare tags of another type.'
  const atRiskTip = 'Tag SKUs at or below their reorder threshold. Buy more of these.'

  return (
    <>
      {/*
        VRG-512: the sample-data caveat — see `IntroDataBanner`'s own docblock for the
        two states and why the affordance to clear stays offered in both.

        MOUNTED ON THIS TAB, not on `TagsPage` above the tab row, which is the one place
        the People precedent does NOT transfer. People puts it above its tabs because
        both tabs read the same tenant's own records; Tags does not. The intro seed
        writes 44 sample chips across two SKUs and three possession states, and every one
        of them lands HERE — in the five cards and the per-SKU table below. The Shop tab
        is Vera's global tag catalogue, identical for every company and holding nothing
        seeded, and the Orders tab reads tag-shop orders from Shopify (`useTagOrders`),
        which the seed never touches. A page-level banner would caption both of those as
        sample data.

        The table below is `fillBottom`, so `useFillBottom` re-measures the grid's own
        top when this mounts (it observes the ancestor chain for exactly this case) and
        the height stays correct. Its `topOffset={428}` is inert while `fillBottom` is
        set except as the pre-measure fallback, so it is deliberately NOT adjusted here:
        the banner renders for some tenants and not others, and a single static number
        cannot be right for both.
      */}
      <IntroDataBanner nouns="tags" />
      {/* The tag pipeline, left to right, then what it buys you: Ordered -> Incoming ->
          Available -> Capacity -> what to reorder. The first three are the possession
          states, so they are mutually exclusive and they ADD UP to every tag the brand owns.

          Ordered is here because without it a brand whose tags are all still at Vera sees
          "Available 0 / Incoming 0" and concludes the page is broken: 100 tags they paid for
          appear nowhere. Showing only the middle stage of a pipeline is worse than showing
          none of it. What it must NOT be is `company_tag_inventory.ordered_qty` — a lifetime
          purchase counter nothing decrements, which cannot reconcile against a possession
          count and so read as a contradiction rather than a stage.

          "Uncertified products" lost its slot to make room. It is demand, not possession or
          risk, and it survives as a (hidden) column and on the gauge's tooltip beside the
          capacity it feeds.

          NO ICONS on any card. Each glyph restated the noun already in its own title — a
          tag beside "tags", a bag beside "products" — spending the card's top-right corner
          on a fact the label states. Same call, same reason, as the non-varying glyphs
          dropped from the scanner app's home and scan-session screens. */}
      <Grid container spacing={3} pb={3}>
        <Grid item md={2.4} sm={6}>
          <AnalyticsBlock title="Ordered" term="inventory-ordered" stat={cards.ordered} />
        </Grid>
        <Grid item md={2.4} sm={6}>
          <AnalyticsBlock title="Incoming" term="inventory-incoming" stat={cards.incoming} />
        </Grid>
        <Grid item md={2.4} sm={6}>
          <AnalyticsBlock title="Available" term="inventory-available" stat={cards.available} />
        </Grid>
        <Grid item md={2.4} sm={6}>
          <AnalyticsBlock title="Capacity" term="inventory-capacity" tooltip={capacityTip} stat={cards.capacity} />
        </Grid>
        <Grid item md={2.4} sm={6}>
          <AnalyticsBlock
            title="Tag SKUs at risk"
            tooltip={atRiskTip}
            stat={cards.atRisk}
          />
        </Grid>
      </Grid>
      <Table
        fillBottom
        topOffset={428}
        rows={inventory}
        columns={columns}
        loading={isLoading}
        showSelection={false}
        selectionDisabled={true}
        // The table's default set MIRRORS THE KPI CARDS above it, in the same order:
        // Ordered · Incoming · Available · (Levels) · Capacity. A card and a column that
        // report the same figure should not disagree about whether it is worth showing —
        // an operator who reads "Incoming 13" in a card and then cannot find Incoming in
        // the table has to go looking for a toggle to see the per-SKU breakdown of a number
        // the page just told them mattered.
        //
        // Incoming and Capacity were both hidden on the previous pass, each for a defensible
        // reason in isolation (Incoming is transient and 0 on most rows; the gauge already
        // draws Capacity). Neither survives the card comparison, which is the stronger
        // constraint.
        //
        // Still hidden — these have NO card, so nothing is contradicted:
        //   uncertified_qty   the gauge's tooltip carries it
        //   products          product-inventory totals, a different subject from tag supply
        visibilityModel={{
          'uncertified_qty': false,
          'products': false,
        }}
        emptyState={
          <EmptyState
            title="You don't have tags yet"
            text="Buy tags in the shop and then statistics on their use will appear here"
            icon={<TagIcon strokeWidth={1} size={32}/>}
            buttons={
              // The empty state's only forward path, so it is the primary. ShoppingCartIcon
              // rather than TagIcon: it is the same "go buy tags" action as the row kebab's
              // Purchase Tags above, and §21 wants one glyph per action across surfaces.
              <Button
                variant="primary"
                disableElevation
                onClick={() => navigate(ROUTES.TAGS_SHOP, { state: referrer })}
                startIcon={<ShoppingCartIcon strokeWidth={1}/>}
              >
                Go to Tag Shop
              </Button>
            }
          />
        }
      />
      {selectedInventory && (
        <>
          <SetInventorySettingsModal
            inventory={selectedInventory}
            open={openInventorySettingsModal}
            onClose={() => setOpenInventorySettingsModal(false)}
            refetch={refetch}
            />
          <SetProductsForTagModal
            tagShopProductId={(selectedInventory as any)?.id ?? ''}
            open={openSetProductsForTagModal}
            onClose={() => setOpenSetProductsForTagModal(false)}
            refetch={refetch}
          />
        </>
      )}
    </>
  )
}
